import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { FormGroup } from "../components/ui/form-group";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const apiUrl = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message;
  const [unconfirmedUser, setUnconfirmedUser] = useState(null);
  const [resendState, setResendState] = useState({ loading: false, message: "" });

  // Initialize formData with empty strings for controlled inputs
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // State for form errors
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  // Handle form data change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password } = formData;

    setUnconfirmedUser(null);
    setResendState({ loading: false, message: "" });

    // Basic validation
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    if (!password) newErrors.password = "Password is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const { success, user, message, unconfirmed } = await login(
      email,
      password,
    );

    if (!success) {
      if (unconfirmed) {
        setUnconfirmedUser(user);
        setErrors({});
      } else {
        setErrors({
          email: message || "The credentials are incorrect",
          password: message || "The credentials are incorrect",
        });
      }
      return;
    }

    // At this point, success is true and user should have veterinary data.
    if (user.veterinary) {
      console.log("User is a veterinary user");
      navigate("/dashboard");
    } else {
      // This block should normally not be reached because login() already checks for veterinary.
      alert("This login is restricted to veterinary users only.");
      return;
    }
  };

  const handleResendConfirmation = async () => {
    if (!unconfirmedUser?.id) return;
    setResendState({ loading: true, message: "" });
    try {
      const response = await axios.post(`${apiUrl}/api/v1/resend_confirmation`, {
        user: { id: unconfirmedUser.id },
      });
      setResendState({
        loading: false,
        message: response.data.message || "Confirmation email sent.",
      });
    } catch (error) {
      setResendState({
        loading: false,
        message:
          error.response?.data?.errors?.[0] ||
          "Failed to resend confirmation email. Please try again.",
      });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.logoContainer}>
        <img src="/carepetai-logo.svg" alt="Logo" style={styles.logo} />
      </div>

      <div style={styles.formContainer}>
        {successMessage && (
          <div style={styles.successBanner}>{successMessage}</div>
        )}

        {unconfirmedUser && (
          <div style={styles.unconfirmedBanner}>
            <p style={styles.unconfirmedText}>
              Please confirm your email before logging in. Check your inbox
              for the confirmation link we sent you.
            </p>
            <button
              type="button"
              onClick={handleResendConfirmation}
              style={styles.resendLink}
              disabled={resendState.loading}
            >
              {resendState.loading
                ? "Sending..."
                : "Resend confirmation email"}
            </button>
            {resendState.message && (
              <p style={styles.resendMessage}>{resendState.message}</p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <FormGroup label="Email" required error={errors.email}>
            <Input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              error={!!errors.email}
            />
          </FormGroup>

          <FormGroup label="Password" required error={errors.password}>
            <Input
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              error={!!errors.password}
            />
          </FormGroup>

          <div style={styles.forgotPasswordContainer}>
            <Link to="/forgot-password" style={styles.forgotPasswordLink}>
              Forgot your password?
            </Link>
          </div>

          <div style={styles.buttonContainer}>
            <Button type="submit" variant="primary" fullWidth size="large">
              Log In
            </Button>
          </div>

          <div style={styles.signUpLinkContainer}>
            <span style={styles.signUpLinkText}>
              Are you a veterinary professional?{" "}
            </span>
            <Link to="/signup" style={styles.signUpLink}>
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "60px 0",
    minHeight: "100vh",
    position: "relative",
    fontFamily: "'Poppins', sans-serif",
    backgroundColor: "#f9fafb",
  },
  logoContainer: {
    textAlign: "center",
    margin: "10px",
  },
  logo: {
    width: "300px",
    height: "50px",
    objectFit: "contain",
  },
  formContainer: {
    padding: "40px",
    maxWidth: "480px",
    margin: "60px auto",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  },
  form: {
    width: "100%",
  },
  forgotPasswordContainer: {
    marginTop: "12px",
    textAlign: "right",
    marginBottom: "20px",
  },
  forgotPasswordLink: {
    background: "none",
    border: "none",
    color: "#F97316",
    fontSize: "14px",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  buttonContainer: {
    marginTop: "24px",
  },
  successBanner: {
    backgroundColor: "#DCFCE7",
    color: "#166534",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "14px",
    marginBottom: "20px",
  },
  signUpLinkContainer: {
    marginTop: "20px",
    textAlign: "center",
    fontSize: "14px",
  },
  signUpLinkText: {
    color: "#6b7280",
  },
  signUpLink: {
    color: "#F97316",
    textDecoration: "underline",
    fontWeight: "500",
  },
  unconfirmedBanner: {
    backgroundColor: "#FEF3C7",
    color: "#92400E",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "14px",
    marginBottom: "20px",
  },
  unconfirmedText: {
    margin: "0 0 8px 0",
  },
  resendLink: {
    background: "none",
    border: "none",
    color: "#92400E",
    fontSize: "14px",
    fontWeight: "600",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  resendMessage: {
    margin: "8px 0 0 0",
    fontSize: "13px",
  },
};

export default LoginPage;
